import React, { useEffect, useRef, useState } from 'react';
import './CursorGlow.css';

export const CursorGlow = () => {
  const canvasRef = useRef(null);
  const [mousePos] = useState({ x: 0.5, y: 0.5 });
  const animationRef = useRef(null);
  const targetPos = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width, height;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e) => {
      const x = e.clientX / width;
      const y = e.clientY / height;
      targetPos.current = { x, y };
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      if (touch) {
        const x = touch.clientX / width;
        const y = touch.clientY / height;
        targetPos.current = { x, y };
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);

    const draw = () => {
      // Плавное следование за курсором
      mousePos.x += (targetPos.current.x - mousePos.x) * 0.08;
      mousePos.y += (targetPos.current.y - mousePos.y) * 0.08;

      ctx.clearRect(0, 0, width, height);

      // Создаём радиальный градиент для свечения
      const centerX = mousePos.x * width;
      const centerY = mousePos.y * height;
      const radius = Math.min(width, height) * 0.45;

      // Основное свечение
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, radius
      );
      
      // Цвета с плавным переходом
      const hue = (mousePos.x + mousePos.y) * 180 + 200;
      gradient.addColorStop(0, `hsla(${hue}, 90%, 60%, 0.15)`);
      gradient.addColorStop(0.3, `hsla(${hue + 40}, 80%, 50%, 0.10)`);
      gradient.addColorStop(0.6, `hsla(${hue + 80}, 70%, 40%, 0.06)`);
      gradient.addColorStop(1, `hsla(${hue + 120}, 60%, 30%, 0)`);

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Рисуем сетку точек (маска)
      const dotSize = 1.5;
      const spacing = 28;
      const glowRadius = radius * 0.7;

      for (let x = 0; x < width; x += spacing) {
        for (let y = 0; y < height; y += spacing) {
          const dx = x - centerX;
          const dy = y - centerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          // Точки внутри круга свечения становятся ярче и цветными
          const maxDist = glowRadius;
          const intensity = Math.max(0, 1 - (dist / maxDist));
          
          // Применяем плавный переход по краям
          const fadeEdge = Math.max(0, Math.min(1, (dist / maxDist - 0.7) / 0.3));
          const smoothIntensity = intensity * (1 - fadeEdge);
          
          if (smoothIntensity > 0.02) {
            // Точки в центре - цветные, на краях - белые
            const huePoint = (hue + dist * 0.05) % 360;
            const alpha = 0.2 + smoothIntensity * 0.6;
            
            if (smoothIntensity > 0.3) {
              ctx.fillStyle = `hsla(${huePoint}, 80%, 70%, ${alpha * 0.5})`;
            } else {
              ctx.fillStyle = `hsla(0, 0%, 100%, ${alpha * 0.3})`;
            }
            
            ctx.beginPath();
            ctx.arc(x, y, dotSize * (0.5 + smoothIntensity * 0.8), 0, Math.PI * 2);
            ctx.fill();
          } else {
            // Фоновые точки (очень тусклые)
            ctx.fillStyle = `hsla(0, 0%, 100%, 0.03)`;
            ctx.beginPath();
            ctx.arc(x, y, dotSize * 0.4, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mousePos]);

  return <canvas ref={canvasRef} className="cursor-glow-canvas" />;
};